"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

import { twMerge } from "tailwind-merge";
import { Icon } from "../Icon";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

type DialogProps = {
  className?: string;
};
const DialogOverlay = React.forwardRef<any, DialogProps>(
  ({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
      ref={ref}
      className={twMerge("fixed inset-0 z-50 bg-black/70", className)}
      {...props}
    />
  ),
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogContentProps = {
  children: React.ReactNode;
  className?: string;
};
const DialogContent = React.forwardRef<any, DialogContentProps>(
  ({ className, children, ...props }, ref) => (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={twMerge(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-4xl translate-x-[-50%] translate-y-[-50%] border bg-background py-6 shadow-lg sm:rounded-2xl",
          className,
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
          <Icon className="w-4 h-4" faIcon={{ style: "fal", name: "xmark" }} />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

type DialogHeaderProps = {
  children: React.ReactNode;
  className?: string;
};
const DialogHeader = ({ className, ...props }: DialogHeaderProps) => (
  <div
    className={twMerge(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }) => (
  <div
    className={twMerge(
      "flex flex-col-reverse sm:flex-row sm:justify-start sm:space-x-2 px-12 border-t pt-6",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

type DialogTitleProps = {
  children: React.ReactNode;
  className?: string;
};
const DialogTitle = React.forwardRef<any, DialogTitleProps>(
  ({ className, ...props }, ref) => (
    <DialogPrimitive.Title
      ref={ref}
      className={twMerge(
        "text-lg font-semibold leading-none tracking-tight border-b px-12 pb-4",
        className,
      )}
      {...props}
    />
  ),
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

type DialogDescriptionProps = {
  children: React.ReactNode;
  className?: string;
};
const DialogDescription = React.forwardRef<any, DialogDescriptionProps>(
  ({ className, ...props }, ref) => (
    <DialogPrimitive.Description
      ref={ref}
      className={twMerge("text-sm text-muted-foreground py-6 px-12", className)}
      {...props}
    />
  ),
);
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
